/**
 * 模組頁面代碼: 1210-UI
 * 模組說明: 派工管理月曆頁面
 * @module DispatchCalendarContainer
 */

// ----- import from local libraries -----
import {
  React,
  PropTypes,
  connect,
  BaseComponent,
  AccessModeEnums,
} from '../../components/BaseComponent';

// ----- import from local components -----
import Header from '../../features/main-frame/components/Header';
import Footer from '../../features/main-frame/components/Footer';
import Sidebar from '../../features/main-frame/components/Sidebar';
import enumList from '../../utils/enumList';
import {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import DispatchCalendarPage from './components/DispatchCalendarPage';
import CalendarModal from './components/CalendarModal';
import {useDispatch} from 'react-redux';
import {fetchDispatchCalendarList} from 'web/redux/actions/DispatchActions';
import {fetchEmployeeList} from 'web/redux/actions/EmployeeActions';

// ----- constants and enums -----

// ----- constants and enums -----

const DispatchCalendarContainer = (props) => {
  const userPermissions = useSelector((state) =>
    JSON.parse(state.access.userPermissions),
  );
  const calendarModalRef = useRef();
  const [calendarModalMode, setCalendarModalMode] = useState(
    enumList.modalMode.new,
  );
  const [eventContent, setEventContent] = useState('');
  const [employeeName, setEmployeeName] = useState('');
  const [isCalendarLoaded, setIsCalendarLoaded] = useState(false);
  const dispatch = useDispatch();
  const [customerSelection, setCustomerSelection] = useState([]);
  const [startTime, setStartTime] = useState(new Date());
  const [endTime, setEndTime] = useState(new Date());
  const [employeeSelection, setEmployeeSelection] = useState([]);
  const [dispatchSelection, setDispatchSelection] = useState([]);

  const [editData, setEditData] = useState({});
  const [calendarReload, setCalendarReload] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const originalEmployeeList = useRef([]);
  const reloadCalendar = () => {
    setCalendarReload((prev) => !prev); // 切換布林值觸發 useEffect
  };

  const setCalendarModalRef = (element) => {
    if (element && !calendarModalRef.current) {
      calendarModalRef.current = new bootstrap.Modal(element);
    }
  };

  useEffect(() => {
    fetchDispatchCalendarData(new Date());
    fetchEmployee();
  }, []);

  const fetchEmployee = (employeeName) => {
    dispatch(
      fetchEmployeeList({employeeName, requireSameDepartment: true}),
    ).then((response) => {
      const statusCode = response?.payload?.code;
      if (statusCode.isSuccessCode()) {
        const employeeSelection = response.payload.result;
        setEmployeeSelection(employeeSelection);
      }
    });
  };

  const getWorkTimeRange = (time) => {
    const startTime = moment(time)
      .subtract(1, 'month')
      .endOf('month')
      .subtract(7, 'day')
      .format('YYYYMMDDHHmmss');
    const endTime = moment(time)
      .add(1, 'month')
      .startOf('month')
      .add(7, 'day')
      .format('YYYYMMDDHHmmss');
    return {startTime, endTime};
  };

  const fetchDispatchs = async (time, employeeName) => {
    return await dispatch(
      fetchDispatchCalendarList({
        ...getWorkTimeRange(time),
        employeeName,
        requireSameDepartment: true,
      }),
    ).unwrap();
  };

  const fetchDispatchCalendarData = async (time, employeeName) => {
    setIsLoading(true);
    const response = await fetchDispatchs(time, employeeName);
    const statusCode = response?.code;
    if (statusCode.isSuccessCode()) {
      const dispatchList = response.result;

      setDispatchSelection(dispatchList);
      const customerSet = new Set();
      const customerAvailableSelection = [];
      dispatchList.forEach((dispatch) => {
        if (
          !dispatch.workEndTime &&
          !dispatch.workStartTime &&
          !customerSet.has(dispatch.customerCode)
        ) {
          customerSet.add(dispatch.customerCode);
          customerAvailableSelection.push({
            customerCode: dispatch.customerCode,
            customerName: dispatch.customerName,
          });
        }
      });
      setCustomerSelection(customerAvailableSelection);
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (originalEmployeeList && originalEmployeeList.current.length === 0) {
      originalEmployeeList.current = employeeSelection;
    }
  }, [employeeSelection, originalEmployeeList]);

  const toggleCalendarModal = (show, mode) => {
    if (show) {
      setCalendarModalMode(mode);
      calendarModalRef.current.show();
    } else {
      calendarModalRef.current.hide();
    }
  };

  const onConfirm = async (req) => {
    toggleCalendarModal(false);
    const {workStartTime} = req;

    const response = await fetchDispatchs(workStartTime);
    const statusCode = response?.code;
    if (statusCode.isSuccessCode()) {
      const dispatchList = response.result;
      setDispatchSelection(dispatchList);
    }
  };

  return (
    <>
      <div className="m__wrap">
        <Header />
        <div className="m__contents">
          <Sidebar />
          <DispatchCalendarPage
            {...props}
            onNewDispatch={(payload) => {
              toggleCalendarModal(true, enumList.modalMode.new);
              if (payload) setEditData(payload);
            }}
            onUpdateDispatch={(payload) => {
              toggleCalendarModal(true, enumList.modalMode.edit);
              setEditData(payload);
            }}
            startTime={startTime}
            endTime={endTime}
            setStartTime={(startTime) => setStartTime(startTime)}
            setEndTime={(endTime) => setEndTime(endTime)}
            isCalendarLoaded={isCalendarLoaded}
            setIsCalendarLoaded={(isCalendarLoaded) =>
              setIsCalendarLoaded(isCalendarLoaded)
            }
            employeeSelection={employeeSelection.filter(
              ({flgStatus}) => flgStatus !== enumList.flgStatus.inactive,
            )}
            setEmployeeCode={(code) => setEmployeeName(code)}
            calendarModalMode={calendarModalMode}
            dispatchSelection={dispatchSelection}
            reload={calendarReload}
            reloadDispatchCalendar={fetchDispatchCalendarData}
            reloadEmployee={fetchEmployee}
            isLoading={isLoading}
          />
        </div>
        <Footer />
      </div>
      <CalendarModal
        ref={setCalendarModalRef}
        mode={calendarModalMode}
        setCalendarModalMode={(mode) => setCalendarModalMode(mode)}
        userPermissions={userPermissions}
        customerSelection={customerSelection}
        employeeSelection={originalEmployeeList.current.filter(
          ({flgStatus}) => flgStatus !== enumList.flgStatus.inactive,
        )}
        employeeCode={employeeName}
        dispatchSelection={dispatchSelection}
        startTime={startTime}
        endTime={endTime}
        editData={editData}
        {...props}
        onAddDispatch={(req) => onConfirm(req)}
      />
    </>
  );
};

export default DispatchCalendarContainer;
